<script setup lang="ts">
	import { useQueryClient } from '@tanstack/vue-query'
	import useVuelidate from '@vuelidate/core'
	import { helpers, required } from '@vuelidate/validators'
	import { computed, reactive, ref, watch } from 'vue'
	import { useToast } from 'vue-toastification'

	import StyledButton from '@/components/StyledButton.vue'
	import StyledCombobox from '@/components/StyledCombobox.vue'
	import StyledInput from '@/components/StyledInput.vue'
	import StyledSelect from '@/components/StyledSelect.vue'

	import useCountryCodes from '@/composables/useCountryCodes'
	import usePhoneRequest from '@/composables/usePhoneRequest'

	import { PhoneTypes } from '@/enums/phoneTypes'

	import { CountryCode } from '@/interfaces/countryCode'

	import useUserPhone from '@/modules/users/composables/useUserPhone'

	import { PhoneConverted } from './UserNotificationsForm.vue'

	const { countryCodes } = useCountryCodes()

	interface FormCountryCode {
		id: number
		value: CountryCode
	}

	interface Props {
		userId: string
		isNotifications?: boolean
		phoneSelected?: PhoneConverted
	}

	interface Emits {
		(e: 'handle-action-modal'): void
		(e: 'handleSave'): void
	}

	interface PhoneForm {
		countryCodeId: number | undefined
		phone: string
		type: PhoneTypes
		extension: string
	}

	const props = withDefaults(defineProps<Props>(), {
		isNotifications: false,
	})
	const emits = defineEmits<Emits>()
	const toast = useToast()
	const { createPhoneAsync } = useUserPhone({
		immediate: false,
	})

	const { updatePhoneMutation } = usePhoneRequest({
		immediate: false,
		params: {
			phoneId: props.phoneSelected?.id,
		},
	})

	const phoneForm = reactive<PhoneForm>({
		countryCodeId: props.phoneSelected?.countryId,
		phone: props.phoneSelected?.number || '',
		type: PhoneTypes.Mobile,
		extension: '',
	})

	const queryClient = useQueryClient()

	const rules = {
		countryCodeId: {
			required: helpers.withMessage('El código de país es requerido', required),
		},
		phone: {
			required: helpers.withMessage('El teléfono es requerido', required),
			requiredIf: helpers.withMessage(
				'El teléfono debe ser de al menos 10 dígitos',
				(value: string) => {
					return value.length >= 10
				}
			),
		},
		type: {
			required: helpers.withMessage(
				'El tipo de teléfono es requerido',
				required
			),
		},
	}

	const v$ = useVuelidate(rules, phoneForm)

	// Variables para el combobox
	const countrySelected = ref<FormCountryCode | undefined>()
	const query = ref('')
	const debouncedQuery = ref('')
	let debounceTimer: ReturnType<typeof setTimeout> | null = null

	// Establecer el país seleccionado si existe en phoneSelected
	if (props.phoneSelected?.countryId) {
		const countryCode = countryCodes.value.find(
			(code) => code.id === props.phoneSelected?.countryId
		)
		if (countryCode) {
			countrySelected.value = {
				id: countryCode.id,
				value: countryCode,
			}
		}
	}

	// Debounce para reducir el lag
	watch(query, (newQuery) => {
		if (debounceTimer) {
			clearTimeout(debounceTimer)
		}
		debounceTimer = setTimeout(() => {
			debouncedQuery.value = newQuery
		}, 150) // 150ms de delay
	})

	// Filtro optimizado de códigos de país
	const filteredCountryCodes = computed(() => {
		if (!debouncedQuery.value || debouncedQuery.value.trim() === '') {
			return countryCodes.value
		}

		const searchTerm = debouncedQuery.value.toLowerCase().trim()

		return countryCodes.value.filter((code) => {
			const codeName = code.name.toLowerCase()
			const codeEnglishName = code.englishName?.toLowerCase() || ''
			const codeIso = code.iso2?.toLowerCase() || code.iso3?.toLowerCase() || ''
			const codeNumber = code.code.toString()

			// Búsqueda por nombre
			if (codeName.includes(searchTerm)) return true

			// Búsqueda por nombre en inglés
			if (codeEnglishName.includes(searchTerm)) return true

			// Búsqueda por código ISO
			if (codeIso.includes(searchTerm)) return true

			// Búsqueda por código numérico
			if (codeNumber.includes(searchTerm)) return true

			// Búsqueda por código con +
			if (
				searchTerm.startsWith('+') &&
				codeNumber.includes(searchTerm.substring(1))
			)
				return true

			// Búsqueda por palabras individuales en el nombre
			const searchWords = searchTerm.split(' ')
			if (searchWords.every((word) => codeName.includes(word))) return true

			return false
		})
	})

	// Opciones mapeadas para el combobox (optimizado)
	const countryCodeOptions = computed(() =>
		filteredCountryCodes.value.map((code) => ({
			id: code.id,
			value: code,
			label: `${code.name} (+${code.code})`,
		}))
	)

	const handlePhoneCreate = async () => {
		for (const field of v$.value.$silentErrors) {
			if (!Array.isArray(field.$message)) {
				toast.error(field.$message)
				return
			}
		}

		if (!phoneForm.countryCodeId) return

		const data = await createPhoneAsync({
			id: props.userId,
			phone: phoneForm.phone,
			extension: phoneForm.extension,
			type: phoneForm.type,
			countryCodeId: phoneForm.countryCodeId,
		})

		if (data) {
			await queryClient.invalidateQueries(['userPhones', props.userId])

			// Si es para notificaciones, también emitir handleSave
			if (props.isNotifications) {
				emits('handleSave')
			}

			emits('handle-action-modal')
		}
	}

	const handlePhoneEdit = async () => {
		for (const field of v$.value.$silentErrors) {
			if (!Array.isArray(field.$message)) {
				toast.error(field.$message)
				return
			}
		}

		if (!phoneForm.countryCodeId) return

		const data = await updatePhoneMutation.mutateAsync({
			phoneId: props.phoneSelected?.id,
			countryCodeId: phoneForm.countryCodeId,
			phone: phoneForm.phone,
		})

		if (data) {
			await queryClient.invalidateQueries(['userPhones', props.userId])

			// Si es para notificaciones, también emitir handleSave
			if (props.isNotifications) {
				emits('handleSave')
			}

			emits('handle-action-modal')
		}
	}
</script>

<template>
	<div class="grid gap-4">
		<div class="flex justify-between">
			<h2 class="text-lg font-medium text-gray-900">
				{{ !phoneSelected ? 'Agregar teléfono' : 'Editar Teléfono' }}
			</h2>
		</div>

		<div class="flex items-center justify-center py-4">
			<form
				class="w-full"
				@submit.prevent="
					!phoneSelected ? handlePhoneCreate() : handlePhoneEdit()
				"
			>
				<div class="flex flex-col gap-4 pb-5">
					<!-- Tipo de teléfono (solo si no es para notificaciones) -->
					<div v-if="!isNotifications">
						<StyledSelect
							:options="
								Object.values(PhoneTypes).map((type) => ({
									label: type,
									value: type,
								}))
							"
							v-model="phoneForm.type"
							placeholder="Selecciona el tipo de teléfono"
							label="Tipo de teléfono"
							name="type"
							:is-required="true"
						/>
					</div>

					<!-- Código de país -->
					<div>
						<StyledCombobox
							v-model="countrySelected"
							:options="countryCodeOptions"
							:prevent-auto-focus="false"
							:prevent-scroll-on-focus="false"
							@input-change="
								(e: string) => {
									query = e
								}
							"
							@model-change="
								(e) => {
									phoneForm.countryCodeId = e?.id!
								}
							"
							label="Código de país"
							placeholder="Busca por nombre o código (ej: México, MEX, +52, 52)"
							:show-input="!countrySelected"
						>
							<template #default="{ option }">
								<div class="flex items-center gap-2">
									<span class="text-lg">{{ option.value.emoji }}</span>
									<span>{{ option.value.name }}</span>
									<span>(+{{ option.value.code }})</span>
								</div>
							</template>

							<template #input-overlap>
								<div class="flex items-center justify-between gap-2 w-full">
									<span>
										{{ countrySelected?.value.emoji }}
										{{ countrySelected?.value.name }} (+{{
											countrySelected?.value.code
										}})
									</span>

									<div
										@click="
											() => {
												countrySelected = undefined
												query = ''
												phoneForm.countryCodeId = undefined
											}
										"
										class="cursor-pointer"
									>
										X
									</div>
								</div>
							</template>
						</StyledCombobox>
					</div>

					<!-- Extensión (solo para teléfonos de oficina) -->
					<div v-if="phoneForm.type === PhoneTypes.Oficina">
						<StyledInput
							v-model="phoneForm.extension"
							label="Extensión"
							placeholder="ej. 101"
							type="text"
						/>
					</div>

					<!-- Número de teléfono -->
					<div>
						<StyledInput
							v-model="phoneForm.phone"
							label="Número de Teléfono"
							placeholder="55 1234 5678"
							type="tel"
							:max-length="16"
							:is-required="true"
						/>
					</div>
				</div>

				<div class="flex items-center justify-end gap-4">
					<StyledButton
						v-if="!phoneSelected"
						type="submit"
						class="bg-[#253C70] text-white flex items-center gap-[0.75rem] !px-2"
					>
						Crear
					</StyledButton>

					<StyledButton
						v-else
						type="submit"
						class="bg-[#253C70] text-white flex items-center gap-[0.75rem] !px-2"
					>
						Editar
					</StyledButton>
				</div>
			</form>
		</div>
	</div>
</template>

<style scoped></style>
